<template>
	<v-menu
		ref="k-menu"
		:attach="attach"
		:bottom="bottom"
		:close-on-content-click="closeOnContentClick"
		:content-class="menuContentClass"
		:disabled="disabled"
		:left="left"
		:light="light"
		:nudge-bottom="nudgeBottom"
		:nudge-left="nudgeLeft"
		:nudge-right="nudgeRight"
		:nudge-top="nudgeTop"
		:offset-x="offsetX"
		:offset-y="offsetY"
		:open-on-hover="openOnHover"
		:return-value="returnValue"
		:right="right"
		:top="top"
		:transition="transition"
		:max-height="maxHeight"
		:max-width="maxWidth"
		:min-width="minWidth"
		:value="menuValue"
		v-bind="forwardedAttrs"
		v-on="forwardedListeners"
		@input="onInput"
	>
		<template v-slot:activator="slotProps">
			<slot name="activator" v-bind="activatorSlotProps(slotProps)"></slot>
		</template>
		<slot></slot>
	</v-menu>
</template>

<script>
const open_accessible_menu_stack = []

export default {
	name: 'KMenu',
	inheritAttrs: false,
	data() {
		return {
			menuValue: this.value,
			activatorElement: null
		}
	},
	props: {
		accessiblePopover: Boolean,
		accessibleLabel: String,
		attach: String,
		ariaHaspopup: String,
		bottom: {
			type: Boolean,
			default: true 
		},
		closeOnContentClick: {
			type: Boolean,
			default: true 
		},
		disabled: Boolean,
		light: Boolean,
		left: Boolean,
		nudgeBottom: [String, Number],
		nudgeLeft: [String, Number],
		nudgeRight: [String, Number],
		nudgeTop: [String, Number],
		offsetX: Boolean,
		offsetY: Boolean,
		openOnHover: Boolean,
		returnValue: null,
		right: Boolean,
		top: Boolean,
		transition:[Boolean, String],
		maxHeight: [String, Number],
		maxWidth: [String, Number],
		minWidth: [String, Number],
		contentId: String,
		parentMenuActive: {
			type: Boolean,
			default: null
		},
		restoreFocusOnClose: {
			type: Boolean,
			default: true
		},
		trapFocus: {
			type: Boolean,
			default: true
		},
		value: Boolean,
	},
	watch: {
		value(newValue) {
			this.menuValue = newValue
		},
		menuValue(is_open) {
			if (!this.accessiblePopover) return

			if (is_open) {
				this.registerOpenMenu()
				this.applyAccessiblePanelAttributes()
				this.focusFirstInteractiveInContent()
				return
			}

			this.unregisterOpenMenu()
			if (this.restoreFocusOnClose) this.restoreActivatorFocus()
		},
		parentMenuActive(is_active) {
			if (!this.accessiblePopover || is_active !== false || !this.menuValue) return
			this.closeMenu()
		}
	},
	computed: {
		forwardedAttrs() {
			const { 'content-class': _contentClass, ...rest } = this.$attrs
			return rest
		},
		forwardedListeners() {
			const { input, ...rest } = this.$listeners
			return rest
		},
		menuContentClass() {
			return [
				this.$attrs['content-class'],
				`k-menu-content-${this._uid}`,
				this.accessiblePopover ? 'k-menu-accessible-popover' : null
			].filter(Boolean).join(' ')
		}
	},
	mounted() {
		document.addEventListener('keydown', this.handleDocumentKeydown, true)
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handleDocumentKeydown, true)
		this.unregisterOpenMenu()
	},
	methods: {
		isVisibleFocusTarget(element) {
			if (!element) return false
			if (element.disabled) return false
			if (element.getAttribute('aria-hidden') === 'true') return false
			if (element.tabIndex < 0) return false
			const styles = window.getComputedStyle(element)
			if (styles.visibility === 'hidden' || styles.display === 'none') return false
			return element.getClientRects().length > 0
		},
		getMenuContentElement() {
			const contentElements = Array.from(document.querySelectorAll(`.k-menu-content-${this._uid}`))
			return contentElements.find(element => element.getClientRects().length > 0) || null
		},
		getFocusContainer() {
			if (this.contentId) {
				const contentById = document.getElementById(this.contentId)
				if (contentById) return contentById
			}
			return this.getMenuContentElement()
		},
		getFocusTargets(container) {
			if (!container) return []
			const focusTargets = Array.from(container.querySelectorAll('button, [href], input, select, textarea, [tabindex]'))
			return focusTargets.filter(element => this.isVisibleFocusTarget(element))
		},
		focusTargetAtIndex(focusTargets, index) {
			if (!focusTargets.length) return
			const lastIndex = focusTargets.length - 1
			const normalizedIndex = index < 0 ? lastIndex : index > lastIndex ? 0 : index
			focusTargets[normalizedIndex].focus()
		},
		moveFocusByOffset(focusTargets, offset) {
			if (!focusTargets.length) return
			const activeIndex = focusTargets.findIndex(element => element === document.activeElement)
			if (activeIndex === -1) {
				this.focusTargetAtIndex(focusTargets, offset > 0 ? 0 : focusTargets.length - 1)
				return
			}
			this.focusTargetAtIndex(focusTargets, activeIndex + offset)
		},
		isSubmenuTrigger(element) {
			const popupType = element?.getAttribute?.('aria-haspopup')
			return Boolean(popupType && popupType !== 'false')
		},
		resolveMenuActionElement(element, container) {
			if (!element) return null
			const candidates = [element, element.closest?.('[aria-haspopup="menu"]'), element.closest?.('button, [href], input, select, textarea, [tabindex]')]
			return candidates.find(candidate => candidate && container?.contains(candidate)) || null
		},
		openFocusedSubmenu(activeElement) {
			const container = this.getFocusContainer()
			const actionElement = this.resolveMenuActionElement(activeElement, container)
			if (!this.isSubmenuTrigger(actionElement)) return false
			if (actionElement.getAttribute('aria-expanded') === 'true') return true
			actionElement.click?.()
			return true
		},
		activateFocusedItem(activeElement) {
			const container = this.getFocusContainer()
			const actionElement = this.resolveMenuActionElement(activeElement, container)
			if (!actionElement) return false
			actionElement.click?.()
			return true
		},
		registerOpenMenu() {
			this.unregisterOpenMenu()
			open_accessible_menu_stack.push(this._uid)
		},
		unregisterOpenMenu() {
			const menuIndex = open_accessible_menu_stack.lastIndexOf(this._uid)
			if (menuIndex >= 0) open_accessible_menu_stack.splice(menuIndex, 1)
		},
		isTopmostAccessibleMenu() {
			return open_accessible_menu_stack[open_accessible_menu_stack.length - 1] === this._uid
		},
		applyAccessiblePanelAttributes() {
			const contentElement = this.getMenuContentElement()
			if (!contentElement) return
			if (this.accessibleLabel) contentElement.setAttribute('aria-label', this.accessibleLabel)
			contentElement.setAttribute('tabindex', '-1')
		},
		focusFirstInteractiveInContent(attempts_remaining = 6) {
			this.$nextTick(() => {
				window.requestAnimationFrame(() => {
					const container = this.getFocusContainer()
					if (!container) return

					if (document.activeElement !== container && !container.contains(document.activeElement)) {
						container.focus()
					}

					const firstFocusTarget = this.getFocusTargets(container).find(element => this.isVisibleFocusTarget(element))
					if (firstFocusTarget) {
						firstFocusTarget.focus()
						return
					}

					if (attempts_remaining > 0) {
						window.setTimeout(() => this.focusFirstInteractiveInContent(attempts_remaining - 1), 16)
					}
				})
			})
		},
		restoreActivatorFocus() {
			this.$nextTick(() => {
				if (this.isVisibleFocusTarget(this.activatorElement)) this.activatorElement.focus()
			})
		},
		recordActivatorElement(event) {
			if (event?.currentTarget instanceof HTMLElement) this.activatorElement = event.currentTarget
		},
		mergeActivatorListener(slotProps, eventName, listener) {
			const originalListener = slotProps.on?.[eventName]
			return (...args) => {
				listener(...args)
				if (!originalListener) return
				if (Array.isArray(originalListener)) originalListener.forEach(fn => fn(...args))
				else originalListener(...args)
			}
		},
		activatorSlotProps(slotProps) {
			const originalAttrs = slotProps.attrs || {}
			return {
				...slotProps,
				on: {
					...(slotProps.on || {}),
					click: this.mergeActivatorListener(slotProps, 'click', this.recordActivatorElement),
					focus: this.mergeActivatorListener(slotProps, 'focus', this.recordActivatorElement),
					keydown: this.mergeActivatorListener(slotProps, 'keydown', this.recordActivatorElement),
					mousedown: this.mergeActivatorListener(slotProps, 'mousedown', this.recordActivatorElement),
				},
				attrs: {
					...originalAttrs,
					...(this.contentId ? { 'aria-controls': this.contentId } : {}),
					'aria-expanded': String(Boolean(this.menuValue)),
					'aria-haspopup': this.ariaHaspopup || originalAttrs['aria-haspopup'] || 'menu'
				}
			}
		},
		handleDocumentKeydown(event) {
			if (!this.accessiblePopover || !this.menuValue || !this.isTopmostAccessibleMenu()) return

			const container = this.getFocusContainer()
			if (!container) return

			if (event.key === 'Escape') {
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				this.closeMenu()
				return
			}

			const focusTargets = this.getFocusTargets(container)
			if (!focusTargets.length) return

			if (event.key === 'ArrowDown') {
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				this.moveFocusByOffset(focusTargets, 1)
				return
			}

			if (event.key === 'ArrowUp') {
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				this.moveFocusByOffset(focusTargets, -1)
				return
			}

			if (event.key === 'ArrowRight') {
				if (!this.openFocusedSubmenu(document.activeElement)) return
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				return
			}

			if (event.key === 'ArrowLeft') {
				if (this.parentMenuActive === null) return
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				this.closeMenu()
				return
			}

			if (event.key === 'Enter' || event.key === ' ') {
				if (!this.activateFocusedItem(document.activeElement)) return
				event.preventDefault()
				event.stopPropagation()
				event.stopImmediatePropagation?.()
				return
			}

			if (event.key !== 'Tab' || !this.trapFocus) return

			event.preventDefault()
			event.stopPropagation()
			event.stopImmediatePropagation?.()

			const activeIndex = focusTargets.findIndex(element => element === document.activeElement)
			const lastIndex = focusTargets.length - 1

			if (event.shiftKey) {
				const previousIndex = activeIndex <= 0 ? lastIndex : activeIndex - 1
				focusTargets[previousIndex].focus()
				return
			}

			const nextIndex = activeIndex === -1 || activeIndex >= lastIndex ? 0 : activeIndex + 1
			focusTargets[nextIndex].focus()
		},
		closeMenu() {
			this.menuValue = false
			this.$emit('input', false)
		},
		onInput(value) {
			this.menuValue = value
			this.$emit('input', value)
		},
		save(value) {
			this.$refs['k-menu'].save(value);
		}
	}
}

</script>
